import React, { useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { useTranslation } from 'react-i18next';

import RoomBookingUserAvatar from './RoomBookingUserAvatar';

export interface PublicReviewItem {
  id: string;
  rating: number;
  comment?: string | null;
  createdAt: string;
  username?: string | null;
  profileImageUrl?: string | null;
}

interface ClickableReviewsSummaryProps {
  averageRating: number;
  reviewCount: number;
  reviews: PublicReviewItem[];
  loading?: boolean;
  onOpen?: () => void | Promise<void>;
  ratingName: string;
  variant?: 'rating' | 'compact';
  stopPropagation?: boolean;
}

const GOLD = '#F8C14A';

export default function ClickableReviewsSummary({
  averageRating,
  reviewCount,
  reviews,
  loading = false,
  onOpen,
  ratingName,
  variant = 'rating',
  stopPropagation = false,
}: ClickableReviewsSummaryProps) {
  const { t, i18n } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [opening, setOpening] = useState(false);

  const sortedReviews = useMemo(
    () =>
      [...reviews].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [reviews],
  );

  const reviewCountLabel = `(${reviewCount} ${t('reviews.reviews', 'reviews')})`;

  const handleOpen = async (event: React.MouseEvent) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    if (reviewCount <= 0) return;

    setOpening(true);
    try {
      await onOpen?.();
    } finally {
      setOpening(false);
    }
    setDialogOpen(true);
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const ratingSx = {
    '& .MuiRating-iconFilled': { color: GOLD },
    '& .MuiRating-iconEmpty': { color: 'action.disabled' },
  } as const;

  return (
    <>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        {variant === 'compact' ? (
          <Stack direction="row" spacing={0} sx={{ display: 'inline-flex' }}>
            {reviewCount > 0 ? (
              <StarRoundedIcon sx={{ color: GOLD, fontSize: 18 }} />
            ) : (
              Array.from({ length: 5 }, (_, index) => (
                <StarBorderRoundedIcon key={index} sx={{ color: 'action.disabled', fontSize: 18 }} />
              ))
            )}
          </Stack>
        ) : (
          <Rating
            name={ratingName}
            value={reviewCount > 0 ? averageRating : 0}
            precision={0.1}
            readOnly
            size="small"
            icon={<StarRoundedIcon fontSize="inherit" />}
            emptyIcon={<StarRoundedIcon fontSize="inherit" />}
            sx={ratingSx}
          />
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            lineHeight: '18px',
          }}
        >
          <span>{averageRating.toFixed(variant === 'compact' ? 2 : 1)}</span>
          {reviewCount > 0 ? (
            <Box
              component="button"
              type="button"
              onClick={handleOpen}
              disabled={opening}
              sx={{
                border: 0,
                bgcolor: 'transparent',
                p: 0,
                m: 0,
                cursor: opening ? 'wait' : 'pointer',
                color: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                fontFamily: 'inherit',
                fontWeight: 'inherit',
                textDecoration: 'underline',
                textUnderlineOffset: 2,
                '&:hover': { color: 'primary.main' },
                '&:disabled': { opacity: 0.7 },
              }}
            >
              {reviewCountLabel}
            </Box>
          ) : (
            <span>{reviewCountLabel}</span>
          )}
        </Typography>
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {t('review.allReviews', 'All Reviews')} ({reviewCount})
            </Typography>
            <IconButton
              aria-label={t('common.close', 'Close')}
              onClick={() => setDialogOpen(false)}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2, pt: 0 }}>
          {reviewCount > 0 ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Rating
                value={averageRating}
                readOnly
                precision={0.1}
                size="small"
                icon={<StarRoundedIcon fontSize="inherit" />}
                emptyIcon={<StarRoundedIcon fontSize="inherit" />}
                sx={ratingSx}
              />
              <Typography variant="body2" color="text.secondary">
                {averageRating.toFixed(1)} · {reviewCount} {t('reviews.reviews', 'reviews')}
              </Typography>
            </Stack>
          ) : null}

          {loading || opening ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : sortedReviews.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('tutorSearch.reviewsEmpty', 'No written reviews yet.')}
            </Typography>
          ) : (
            <Stack spacing={2} divider={<Divider flexItem />}>
              {sortedReviews.map((review) => (
                <Box key={review.id}>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <RoomBookingUserAvatar
                      userProfileImageUrl={review.profileImageUrl}
                      displayName={review.username?.trim() || '?'}
                      size={36}
                    />
                    <Box minWidth={0} sx={{ flex: 1 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mb: 0.5 }}
                      >
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {review.username?.trim() ||
                            t('review.anonymousReviewer', 'Anonymous student')}
                        </Typography>
                        <Rating value={review.rating} readOnly size="small" precision={0.1} sx={ratingSx} />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(review.createdAt)}
                        </Typography>
                      </Stack>
                      {review.comment?.trim() ? (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {review.comment.trim()}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
